import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Chip } from '@mui/material';
import {
  Home as HomeIcon,
  AccountTree as TreeIcon,
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Chat as ChatIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SystemConfigService } from '../../services/systemConfigService';

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  requiresAdmin?: boolean;
  requiresFeature?: string;
}

interface NavigationVariantProps {
  variant: 'current' | 'pill' | 'underline' | 'elevated' | 'icon-badge' | 'segmented' | 'subtle';
  isMobile?: boolean;
  onNavigate?: () => void;
  demoMode?: boolean;
  demoActivePath?: string;
}

const baseMenuItems: MenuItem[] = [
  { label: 'Home', path: '/', icon: <HomeIcon /> },
  { label: 'Family Tree', path: '/tree', icon: <TreeIcon /> },
  { label: 'People', path: '/people', icon: <PeopleIcon /> },
  { label: 'Timeline', path: '/timeline', icon: <TimelineIcon />, requiresFeature: 'enableMasterTimeline' },
  { label: 'Messages', path: '/messages', icon: <ChatIcon /> },
  { label: 'Admin', path: '/admin', icon: <AdminIcon />, requiresAdmin: true },
];

export const NavigationVariant: React.FC<NavigationVariantProps> = ({
  variant,
  isMobile = false,
  onNavigate,
  demoMode = false,
  demoActivePath = '/people',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState } = useAuth();
  const [enabledFeatures, setEnabledFeatures] = useState<Partial<Record<string, boolean>>>({});

  // Load feature flags
  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const config = await SystemConfigService.getConfig();
        setEnabledFeatures(config.features as unknown as Partial<Record<string, boolean>> || {});
      } catch (error) {
        console.error('Failed to load feature flags:', error);
      }
    };

    loadFeatures();
  }, []);

  const isItemVisible = (item: MenuItem): boolean => {
    if (item.requiresAdmin) {
      return authState.user?.role === 'admin';
    }
    if (item.requiresFeature) {
      return enabledFeatures[item.requiresFeature] === true;
    }
    return true;
  };

  const isActive = (path: string): boolean => {
    // In demo mode, use the demo active path
    if (demoMode) {
      if (path === '/') {
        return demoActivePath === '/';
      }
      return demoActivePath.startsWith(path);
    }

    // Normal mode - use actual location
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string) => {
    // In demo mode, don't navigate
    if (demoMode) {
      return;
    }

    navigate(path);
    if (onNavigate) {
      onNavigate();
    }
  };

  // VARIANT 1: CURRENT STYLE
  if (variant === 'current') {
    return (
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0.5 }}>
        {baseMenuItems.filter(isItemVisible).map((item) => {
          const active = isActive(item.path);
          return (
            <Button
              key={item.path}
              startIcon={item.icon}
              onClick={() => handleNavigation(item.path)}
              sx={{
                color: active ? 'primary.main' : 'text.primary',
                backgroundColor: active ? 'primary.50' : 'transparent',
                fontWeight: active ? 600 : 500,
                borderRadius: 2,
                px: 2,
                py: 1,
                justifyContent: isMobile ? 'flex-start' : 'center',
                minWidth: isMobile ? 'auto' : 100,
                '&:hover': { backgroundColor: active ? 'primary.100' : 'action.hover' },
                transition: 'all 0.2s ease-in-out',
                ...(active && {
                  borderBottom: !isMobile ? '2px solid' : 'none',
                  borderColor: 'primary.main',
                  borderLeft: isMobile ? '3px solid' : 'none',
                }),
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Box>
    );
  }

  // VARIANT 2: PILL/ROUNDED STYLE
  if (variant === 'pill') {
    return (
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
        {baseMenuItems.filter(isItemVisible).map((item) => {
          const active = isActive(item.path);
          return (
            <Button
              key={item.path}
              startIcon={item.icon}
              onClick={() => handleNavigation(item.path)}
              sx={{
                color: active ? 'primary.contrastText' : 'text.primary',
                backgroundColor: active ? 'primary.main' : 'transparent',
                fontWeight: 600,
                borderRadius: 8,
                px: 3,
                py: 1.25,
                textTransform: 'none',
                fontSize: '0.95rem',
                justifyContent: isMobile ? 'flex-start' : 'center',
                minWidth: isMobile ? 'auto' : 110,
                '&:hover': {
                  backgroundColor: active ? 'primary.dark' : 'action.hover',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: active ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Box>
    );
  }

  // VARIANT 3: MINIMAL UNDERLINE STYLE
  if (variant === 'underline') {
    return (
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 0.5 : 0 }}>
        {baseMenuItems.filter(isItemVisible).map((item) => {
          const active = isActive(item.path);
          return (
            <Button
              key={item.path}
              startIcon={item.icon}
              onClick={() => handleNavigation(item.path)}
              sx={{
                color: active ? 'primary.main' : 'text.secondary',
                backgroundColor: 'transparent',
                fontWeight: active ? 600 : 500,
                borderRadius: 0,
                px: 2.5,
                py: 1.5,
                position: 'relative',
                textTransform: 'none',
                justifyContent: isMobile ? 'flex-start' : 'center',
                minWidth: isMobile ? 'auto' : 100,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: active ? 'translateX(-50%) scaleX(1)' : 'translateX(-50%) scaleX(0)',
                  width: '80%',
                  height: '3px',
                  backgroundColor: 'primary.main',
                  borderRadius: '3px 3px 0 0',
                  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                },
                '&:hover::after': {
                  transform: 'translateX(-50%) scaleX(1)',
                },
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Box>
    );
  }

  // VARIANT 4: MODERN ELEVATED STYLE
  if (variant === 'elevated') {
    return (
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1.5 }}>
        {baseMenuItems.filter(isItemVisible).map((item) => {
          const active = isActive(item.path);
          return (
            <Button
              key={item.path}
              startIcon={item.icon}
              onClick={() => handleNavigation(item.path)}
              sx={{
                color: active ? 'primary.main' : 'text.primary',
                backgroundColor: active ? 'background.paper' : 'transparent',
                fontWeight: active ? 600 : 500,
                borderRadius: 3,
                px: 2.5,
                py: 1.5,
                textTransform: 'none',
                border: '1px solid',
                borderColor: active ? 'primary.main' : 'transparent',
                justifyContent: isMobile ? 'flex-start' : 'center',
                minWidth: isMobile ? 'auto' : 110,
                '&:hover': {
                  backgroundColor: 'background.paper',
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Box>
    );
  }

  // VARIANT 5: ICON BADGE STYLE
  if (variant === 'icon-badge') {
    return (
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
        {baseMenuItems.filter(isItemVisible).map((item) => {
          const active = isActive(item.path);
          return (
            <Button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                color: active ? 'primary.main' : 'text.secondary',
                backgroundColor: 'transparent',
                fontWeight: active ? 600 : 500,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                minWidth: isMobile ? 'auto' : 90,
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'primary.main',
                  '& .icon-wrapper': {
                    backgroundColor: 'primary.light',
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >
              <Box
                className="icon-wrapper"
                sx={{
                  backgroundColor: active ? 'primary.main' : 'action.hover',
                  color: active ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {item.icon}
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'inherit' }}>
                {item.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
    );
  }

  // VARIANT 6: SEGMENTED CONTROL STYLE
  if (variant === 'segmented') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          backgroundColor: 'action.hover',
          borderRadius: 2,
          p: 0.5,
          gap: 0.5,
        }}
      >
        {baseMenuItems.filter(isItemVisible).map((item) => {
          const active = isActive(item.path);
          return (
            <Button
              key={item.path}
              startIcon={item.icon}
              onClick={() => handleNavigation(item.path)}
              sx={{
                color: active ? 'primary.main' : 'text.secondary',
                backgroundColor: active ? 'background.paper' : 'transparent',
                fontWeight: active ? 600 : 500,
                borderRadius: 1.5,
                px: 2.5,
                py: 1,
                textTransform: 'none',
                flex: 1,
                justifyContent: isMobile ? 'flex-start' : 'center',
                minWidth: isMobile ? 'auto' : 100,
                '&:hover': {
                  backgroundColor: active ? 'background.paper' : 'action.selected',
                },
                transition: 'all 0.2s ease-in-out',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Box>
    );
  }

  // VARIANT 7: SUBTLE BACKGROUND STYLE
  if (variant === 'subtle') {
    return (
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 0.5 }}>
        {baseMenuItems.filter(isItemVisible).map((item) => {
          const active = isActive(item.path);
          return (
            <Button
              key={item.path}
              startIcon={item.icon}
              onClick={() => handleNavigation(item.path)}
              sx={{
                color: active ? 'primary.main' : 'text.primary',
                backgroundColor: active ? 'primary.light' : 'transparent',
                backgroundImage: active
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)'
                  : 'none',
                fontWeight: active ? 600 : 500,
                borderRadius: 2,
                px: 2.5,
                py: 1.25,
                textTransform: 'none',
                position: 'relative',
                overflow: 'hidden',
                justifyContent: isMobile ? 'flex-start' : 'center',
                minWidth: isMobile ? 'auto' : 110,
                '&:hover': {
                  backgroundColor: active ? 'primary.light' : 'action.hover',
                },
                '&::before': active ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  backgroundColor: 'primary.main',
                  borderRadius: '0 4px 4px 0',
                } : {},
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Box>
    );
  }

  return null;
};

// Demo component to show all variants
export const NavigationStyleDemo: React.FC = () => {
  const variants = [
    { key: 'current', name: 'Current Style', description: 'Your existing navigation style' },
    { key: 'pill', name: 'Pill Style', description: 'Rounded buttons with filled active state' },
    { key: 'underline', name: 'Minimal Underline', description: 'Clean with animated underline' },
    { key: 'elevated', name: 'Modern Elevated', description: 'Cards with hover elevation' },
    { key: 'icon-badge', name: 'Icon Badge', description: 'Vertical layout with icon badges' },
    { key: 'segmented', name: 'Segmented Control', description: 'Connected buttons like iOS' },
    { key: 'subtle', name: 'Subtle Background', description: 'Soft colors with side accent' },
  ];

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h3" gutterBottom sx={{ mb: 4 }}>
        Navigation Button Styles
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Browse through different navigation styles and pick your favorite!
      </Typography>
      <Typography variant="body2" color="primary.main" sx={{ mb: 6, fontWeight: 500 }}>
        💡 The "People" button is shown as active in each example so you can see the selected state styling.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {variants.map((variant) => (
          <Box
            key={variant.key}
            sx={{
              p: 4,
              backgroundColor: 'background.paper',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h5" fontWeight={600}>
                  {variant.name}
                </Typography>
                <Chip
                  label={variant.key === 'current' ? 'Current' : 'Preview'}
                  size="small"
                  color={variant.key === 'current' ? 'default' : 'primary'}
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {variant.description}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 3,
                backgroundColor: 'background.default',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <NavigationVariant
                variant={variant.key as any}
                demoMode={true}
                demoActivePath="/people"
              />
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 6, p: 4, backgroundColor: 'info.light', borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>
          How to choose?
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>
            <li><strong>Pill Style:</strong> Great for modern, friendly interfaces</li>
            <li><strong>Minimal Underline:</strong> Perfect for clean, professional look</li>
            <li><strong>Elevated:</strong> Adds depth and dimension to your navigation</li>
            <li><strong>Icon Badge:</strong> Best when you want icons to be prominent</li>
            <li><strong>Segmented:</strong> iOS-style for a cohesive, grouped feel</li>
            <li><strong>Subtle:</strong> Elegant with understated active states</li>
          </ul>
        </Typography>
      </Box>
    </Box>
  );
};
